import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert, Box, Button, CircularProgress, IconButton, InputAdornment,
  LinearProgress, Stack, TextField, Typography,
} from '@mui/material';
import { Visibility, VisibilityOff, CheckCircle } from '@mui/icons-material';
import apiClient from '../../api/client';
import AuthLayout from './AuthLayout';
import { LoadingState } from '../../components/ui/States';

interface UserInfo {
  firstName: string;
  lastName: string;
  email: string;
  role: string;
}

interface AccountInfo {
  companyName: string;
  role: string;
}

/** Mirrors PASSWORD_VALIDATION in contracts, enforced by the backend. */
const PASSWORD_RULES: { label: string; test: (p: string) => boolean }[] = [
  { label: '8+ characters', test: (p) => p.length >= 8 },
  { label: 'an uppercase letter', test: (p) => /[A-Z]/.test(p) },
  { label: 'a lowercase letter', test: (p) => /[a-z]/.test(p) },
  { label: 'a number', test: (p) => /[0-9]/.test(p) },
  { label: 'a special character', test: (p) => /[!@#$%^&*(),.?":{}|<>]/.test(p) },
];

const AcceptInvite: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [accountInfo, setAccountInfo] = useState<AccountInfo | null>(null);
  const [isExistingUser, setIsExistingUser] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Password strength calculation
  const getPasswordStrength = (pwd: string): number => {
    let strength = 0;
    if (pwd.length >= 8) strength += 25;
    if (pwd.length >= 12) strength += 25;
    if (/[a-z]/.test(pwd) && /[A-Z]/.test(pwd)) strength += 25;
    if (/[0-9]/.test(pwd)) strength += 15;
    if (/[^a-zA-Z0-9]/.test(pwd)) strength += 10;
    return Math.min(strength, 100);
  };

  const passwordStrength = getPasswordStrength(password);
  const getStrengthColor = () => {
    if (passwordStrength < 40) return 'error';
    if (passwordStrength < 70) return 'warning';
    return 'success';
  };

  const getStrengthLabel = () => {
    if (passwordStrength < 40) return 'Weak';
    if (passwordStrength < 70) return 'Medium';
    return 'Strong';
  };

  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setError('Invalid invitation link. Please check your email for the correct link.');
        setLoading(false);
        return;
      }

      try {
        const response = await apiClient.get<any, { valid: boolean; user: UserInfo; account: AccountInfo; isExistingUser: boolean }>(`/api/invitations/validate/${token}`);
        
        if (response.valid) {
          setUserInfo(response.user);
          setAccountInfo(response.account);
          setIsExistingUser(response.isExistingUser || false);
          console.log('✅ Token validated:', { isExistingUser: response.isExistingUser, company: response.account?.companyName });
        } else {
          setError('This invitation link is invalid or has expired.');
        }
      } catch (err: any) {
        console.error('Token validation error:', err);
        setError(err.response?.data?.message || 'This invitation link is invalid or has expired.');
      } finally {
        setLoading(false);
      }
    };

    validateToken();
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // For new users, validate password
    if (!isExistingUser) {
      // Mirrors src/utils/passwordPolicy.js on the backend, which is the
      // enforcement point. The old check was a length test plus a strength
      // score that let "abcdefghijkl" through, so the server would now reject
      // passwords this page had accepted.
      const unmet = PASSWORD_RULES.filter((rule) => !rule.test(password));
      if (unmet.length > 0) {
        setError(`Password still needs: ${unmet.map((r) => r.label).join(', ')}.`);
        return;
      }

      if (password !== confirmPassword) {
        setError('Passwords do not match');
        return;
      }
    }

    setSubmitting(true);

    try {
      await apiClient.post<any, { message: string; isExistingUser: boolean }>('/api/invitations/accept', {
        token,
        password: isExistingUser ? undefined : password, // Only send password for new users
      });

      setSuccess(true);
      
      // Redirect to login after 2 seconds
      const message = isExistingUser 
        ? `Successfully joined ${accountInfo?.companyName}! Please sign in to access your new company.`
        : 'Account activated! Please sign in with your credentials.';
      
      setTimeout(() => {
        navigate('/login', { 
          state: { 
            message,
            email: userInfo?.email 
          } 
        });
      }, 2000);
    } catch (err: any) {
      console.error('Accept invitation error:', err);
      setError(err.response?.data?.message || 'Failed to activate account. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <AuthLayout title="Checking your invitation…">
        <LoadingState label="One moment." height={120} />
      </AuthLayout>
    );
  }

  if (error && !userInfo) {
    return (
      <AuthLayout
        title="That invitation is not valid"
        subtitle="It may have been revoked, already used, or expired."
      >
        <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Ask whoever invited you to send a new one.
        </Typography>
        <Button fullWidth variant="contained" onClick={() => navigate('/login')}>
          Go to sign in
        </Button>
      </AuthLayout>
    );
  }

  if (success) {
    return (
      <AuthLayout title={isExistingUser ? `Welcome to ${accountInfo?.companyName}` : 'Account activated'}>
        <Stack alignItems="center" gap={2} sx={{ textAlign: 'center', py: 1 }}>
          <CheckCircle sx={{ fontSize: 48, color: 'success.main' }} />
          <Typography variant="body2" color="text.secondary">
            {isExistingUser
              ? 'You have joined the company. Taking you to sign in…'
              : 'Your account is ready. Taking you to sign in…'}
          </Typography>
          <CircularProgress size={20} />
        </Stack>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      wide
      title={isExistingUser ? `Join ${accountInfo?.companyName}` : 'Set up your account'}
      subtitle={isExistingUser ? undefined : 'Choose a password and you are in.'}
    >
      <>
            
            {userInfo && (
              <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Name:</strong> {userInfo.firstName} {userInfo.lastName}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Email:</strong> {userInfo.email}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Role:</strong> {accountInfo?.role || userInfo.role}
                </Typography>
                {accountInfo?.companyName && (
                  <Typography variant="body2" color="text.secondary">
                    <strong>Company:</strong> {accountInfo.companyName}
                  </Typography>
                )}
              </Box>
            )}

            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              {isExistingUser 
                ? `You've been invited to join ${accountInfo?.companyName}! Click the button below to accept this invitation.`
                : 'Please create a secure password to activate your account.'
              }
            </Typography>
            
            {isExistingUser && (
              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="body2">
                  <strong>Note:</strong> You already have a TopoSDS account. After accepting this invitation, you'll be able to switch between companies using the account switcher.
                </Typography>
              </Alert>
            )}

            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit}>
              {!isExistingUser && (
                <>
                  <TextField
                    label="Password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    fullWidth
                    required
                    disabled={submitting}
                    sx={{ mb: 1 }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />

                  {password && (
                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="caption" color="text.secondary">
                          Password Strength:
                        </Typography>
                        <Typography variant="caption" color={`${getStrengthColor()}.main`}>
                          {getStrengthLabel()}
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={passwordStrength}
                        color={getStrengthColor()}
                        sx={{ height: 6, borderRadius: 3 }}
                      />
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                        Use 8+ characters with uppercase, lowercase, numbers, and symbols
                      </Typography>
                    </Box>
                  )}

                  <TextField
                    label="Confirm Password"
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    fullWidth
                    required
                    disabled={submitting}
                    sx={{ mb: 3 }}
                    error={confirmPassword !== '' && password !== confirmPassword}
                    helperText={
                      confirmPassword !== '' && password !== confirmPassword
                        ? 'Passwords do not match'
                        : ''
                    }
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            edge="end"
                          >
                            {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </>
              )}

              <Button
                type="submit"
                variant="contained"
                fullWidth
                size="large"
                disabled={submitting || (!isExistingUser && (!password || !confirmPassword || password !== confirmPassword))}
                sx={{ mb: 2 }}
              >
                {submitting ? (
                  <>
                    <CircularProgress size={24} color="inherit" sx={{ mr: 1 }} />
                    {isExistingUser ? 'Accepting Invitation...' : 'Activating Account...'}
                  </>
                ) : (
                  isExistingUser ? 'Accept Invitation' : 'Activate Account'
                )}
              </Button>

              <Typography variant="body2" color="text.secondary" textAlign="center">
                {isExistingUser
                  ? 'After accepting, you can sign in and switch between your companies.'
                  : 'After activation, you can sign in with your email and password.'
                }
              </Typography>
            </Box>
      </>
    </AuthLayout>
  );
};

export default AcceptInvite;
